"use client"

import { useCallback } from "react"
import { useAccount, useChainId, useDisconnect, useSwitchChain } from "wagmi"

import {
  shortenAddress,
  walletChainIdByNumericId,
  walletChains,
  type WalletChainId,
  type WalletModalLabels,
} from "../config"
import { useWalletChain } from "../context/wallet-context"
import { useTronWallet } from "./use-tron-wallet"

export function useWallet() {
  const { activeChain, setActiveChain, openWalletModal } = useWalletChain()
  const { address, isConnected: evmConnected } = useAccount()
  const chainId = useChainId()
  const { disconnect: disconnectEvm } = useDisconnect()
  const { switchChainAsync } = useSwitchChain()
  const tron = useTronWallet()

  const isTron = activeChain === "tron"
  const isConnected = isTron ? tron.isConnected : evmConnected
  const resolvedAddress = isTron ? tron.address : address
  const shortAddress = resolvedAddress ? shortenAddress(resolvedAddress) : null

  const chain =
    !isTron && walletChainIdByNumericId[chainId]
      ? walletChains[walletChainIdByNumericId[chainId]]
      : walletChains[activeChain]

  const openModal = useCallback(
    (labels?: Partial<WalletModalLabels>) => {
      openWalletModal(labels)
    },
    [openWalletModal]
  )

  const connect = useCallback(
    (labels?: Partial<WalletModalLabels>) => {
      openWalletModal(labels)
    },
    [openWalletModal],
  )

  const disconnect = useCallback(async () => {
    if (isTron) {
      tron.disconnect()
      return
    }
    disconnectEvm()
  }, [disconnectEvm, isTron, tron])

  const switchNetwork = useCallback(
    async (target: WalletChainId) => {
      setActiveChain(target)
      if (target === "tron") {
        if (tron.hasTronLink) {
          await tron.connect()
        } else {
          openWalletModal()
        }
        return
      }
      const targetChainId = walletChains[target].chainId
      if (evmConnected && targetChainId) {
        await switchChainAsync({ chainId: targetChainId })
      }
    },
    [evmConnected, openWalletModal, setActiveChain, switchChainAsync, tron],
  )

  return {
    address: resolvedAddress ?? null,
    shortAddress,
    chain,
    activeChain,
    isConnected,
    connect,
    openModal,
    switchNetwork,
    disconnect,
    setActiveChain,
  }
}
